// Walls in the way of a move (issue 1144, slice 1; design/30-sight-walls-and-light.md §1.1, §1.2, §1.6). Pure
// geometry in board pixels: the server's check of every move and the browser's refusal of a drag before it is sent
// run this one code, so the preview and the authority cannot disagree about the walls the screen holds (a player's
// screen holds none yet, and is told by the server's refusal).
//
// Touching counts as crossing: a route that passes through a wall's end, grazes it, or runs along it is in the way.
// This is design/30 §1.6's rule for sight ("when the rule is unsure it errs on the secret's side"), used for movement
// too, so a token can never slip through the corner where two walls meet.
/** Which side of the line a→b point c lies on: >0 left, <0 right, 0 on it. */
const orient = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
/** Whether c, known to be on the line through a and b, lies within the box they span. */
const within = (a, b, c) => Math.min(a.x, b.x) <= c.x && c.x <= Math.max(a.x, b.x) && Math.min(a.y, b.y) <= c.y && c.y <= Math.max(a.y, b.y);
/**
 * Whether segment p1–p2 meets segment q1–q2 anywhere, ends included: a proper crossing, an end touching the other
 * segment, or a collinear overlap. Parallel segments that do not overlap do not meet.
 */
export function segmentsMeet(p1, p2, q1, q2) {
    const d1 = orient(q1, q2, p1);
    const d2 = orient(q1, q2, p2);
    const d3 = orient(p1, p2, q1);
    const d4 = orient(p1, p2, q2);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0)))
        return true;
    if (d1 === 0 && within(q1, q2, p1))
        return true;
    if (d2 === 0 && within(q1, q2, p2))
        return true;
    if (d3 === 0 && within(p1, p2, q1))
        return true;
    if (d4 === 0 && within(p1, p2, q2))
        return true;
    return false;
}
/** Whether a wall stops movement: a closed door always (design/30 §1.2: a wall with both switches on), an open one never, otherwise its switch. */
export const blocksMove = (w) => (w.door ? !w.door.open : w.move);
/** Whether a wall stops sight (and light): the same rule with the other switch. */
export const blocksSight = (w) => (w.door ? !w.door.open : w.sight);
/** What a person is told when a move is refused because a wall is in the way. */
export const WALL_IN_THE_WAY = 'A wall is in the way.';
/**
 * Whether a move from `from` along `route` (the points the hand passed, the last where it ends) meets a wall that
 * blocks movement. Each leg — from the start to the first point, then point to point — is tested against each wall.
 */
export function routeBlocked(from, route, walls) {
    const blocking = walls.filter(blocksMove);
    if (!blocking.length)
        return false;
    let at = from;
    for (const p of route) {
        if (p.x !== at.x || p.y !== at.y) {
            for (const w of blocking)
                if (segmentsMeet(at, p, w.a, w.b))
                    return true;
        }
        at = p;
    }
    return false;
}
/** The distance in pixels from point p to segment a–b: what a click in wall mode is tested against. */
export function distanceToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
//# sourceMappingURL=walls.js.map